import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SiteNav from "../components/Layout/SiteNav";
import useHalalFeature from "../hooks/useHalalFeature";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const HERO_CHIPS = ["Role-based access", "Real-time updates", "Proximity outreach"];

const WHY_FEATURES = [
  {
    n: "01",
    t: "Prayer Times Control",
    b: "Edit adhan and iqamah times, publish updates instantly, and display them across TV and mobile.",
    ar: "الصلاة",
  },
  {
    n: "02",
    t: "Events & Calendars",
    b: "Create events, sync Google Calendars with filters and offsets, and keep your schedule centralized.",
    ar: "فعالية",
  },
  {
    n: "03",
    t: "Announcements & Notifications",
    b: "Send push notifications and proximity-based announcements so your community never misses an update.",
    ar: "الإعلان",
  },
  {
    n: "04",
    t: "Team Roles",
    b: "Add Admins and Admin Owners with clear permissions to keep oversight of your masjid.",
    ar: "فريق",
  },
];

const TOOLKIT = [
  {
    t: "TV Display",
    b: "Launch a fullscreen TV view to show live prayer times and events inside your masjid.",
    badge: null,
  },
  {
    t: "Website Widget",
    b: "Embed a responsive prayer-times widget on your site with your colors and theme.",
    badge: null,
  },
  {
    t: "Monthly PDF",
    b: "Generate printable monthly prayer time PDFs directly from the dashboard.",
    badge: "Pro",
  },
  {
    t: "Nearby Notifications",
    b: "Send targeted push alerts to users near your masjid.",
    badge: "Pro",
  },
];

const PLANS = [
  {
    n: "Basic",
    price: "Free",
    tag: "Included",
    feats: [
      "Manage prayer times",
      "Send notifications",
      "Manage admins",
      "Manage events",
      "Google Calendar integration",
    ],
    dark: false,
    cta: "Register free",
  },
  {
    n: "Pro",
    price: "$25/mo",
    tag: "Available now",
    feats: [
      "Nearby notifications",
      "Monthly prayer times PDF",
      "Proximity announcements (20 miles)",
      "Event ticket management",
      "Website widget",
    ],
    dark: true,
    cta: "Start Pro trial",
  },
];

const DAY_ONE = [
  {
    t: "Stay Accurate",
    b: "Refresh prayer times, adjust offsets, and sync to your calendars and displays.",
  },
  {
    t: "Engage Members",
    b: "Publish events, generate flyers, manage tickets, and push notifications to attendees.",
  },
  {
    t: "Collaborate Safely",
    b: "Add admins and owners, track activity, and keep sensitive controls with the right roles.",
  },
];

const REASSURANCE = [
  {
    t: "Built for masjid teams",
    b: "Mobile and web tools you'll use daily.",
  },
  {
    t: "Keep your community informed",
    b: "Push announcements, upcoming events, and calendar updates.",
  },
  {
    t: "Trusted access control",
    b: "Role-based access for Admins and Admin Owners to keep sensitive tools protected.",
  },
];

export default function MasjidInfo() {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const palette = useEditorialPalette();
  const { halalEnabled } = useHalalFeature();

  const getDashboardRoute = () => {
    if (!user) return "/login";
    if (userProfile?.role === "super") return "/dashboard";
    if (userProfile?.role === "halal") return "/halal";
    if (userProfile?.isAdmin) return "/masjid-dashboard";
    return "/";
  };

  const handlePrimaryCta = () => {
    if (!authLoading && user && userProfile) {
      navigate(getDashboardRoute());
      return;
    }
    navigate("/signup");
  };

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ background: palette.ivory }}>
        <PatternTile opacity={0.05} />
        <div
          className="relative mx-auto px-4 pt-16 pb-16 text-center sm:px-6 sm:pt-24 sm:pb-20 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <div className="mb-6 inline-flex">
            <Eyebrow>For masjid administrators</Eyebrow>
          </div>
          <SerifHeading
            level={1}
            className="mb-6"
            style={{
              fontSize: "clamp(2.75rem, 7.5vw, 7rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.035em",
            }}
          >
            Bring your masjid
            <br />
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>online.</em>
          </SerifHeading>
          <p
            className="mx-auto mb-4 max-w-2xl text-base sm:text-lg md:text-xl"
            style={{ color: palette.ink2, lineHeight: 1.5 }}
          >
            Manage and grow with Muslim+.
          </p>
          <p
            className="mx-auto mb-9 max-w-2xl text-base sm:text-lg"
            style={{ color: palette.ink2, lineHeight: 1.6 }}
          >
            Control prayer times, events, and announcements in one place. Keep your community
            connected through the app, TV displays, and your website.
          </p>
          <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:justify-center sm:flex-wrap">
            <PillButton variant="primary" onClick={handlePrimaryCta}>
              Register your masjid
              <span aria-hidden>→</span>
            </PillButton>
            <PillButton variant="ghost" onClick={() => navigate("/masjids")}>
              Browse masajid near you
            </PillButton>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
            {HERO_CHIPS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] sm:text-[13px]"
                style={{
                  background: "transparent",
                  border: `1px solid ${palette.hairline}`,
                  color: palette.ink2,
                  fontFamily: EDITORIAL_FONT.sans,
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: BRAND.gold,
                  }}
                />
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          WHY MASJID ADMINS CHOOSE MUSLIM+
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center lg:gap-16">
            <div className="text-center lg:text-left">
              <Eyebrow>Why masjid admins choose Muslim+</Eyebrow>
              <SerifHeading
                className="mt-5"
                style={{
                  fontSize: "clamp(2rem, 4.5vw, 4rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                }}
              >
                Built around how
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>your team actually works.</em>
              </SerifHeading>
            </div>
            <p
              className="mx-auto max-w-xl text-center text-base sm:text-lg lg:mx-0 lg:text-left"
              style={{ color: palette.ink2, lineHeight: 1.55 }}
            >
              Capabilities informed by our mobile app tools and admin dashboard. Designed for
              volunteer teams who manage the masjid between prayers.
            </p>
          </div>

          <div
            className="grid grid-cols-1 border-t md:grid-cols-2"
            style={{ borderColor: palette.hairline }}
          >
            {WHY_FEATURES.map((f, i) => (
              <div
                key={f.n}
                className="relative px-7 py-10"
                style={{
                  borderRight: i % 2 === 0 ? `1px solid ${palette.hairline}` : "none",
                  borderBottom: `1px solid ${palette.hairline}`,
                }}
              >
                <div className="mb-6 flex items-center justify-end">
                  <div
                    style={{
                      fontFamily: EDITORIAL_FONT.arabic,
                      fontSize: 22,
                      color: BRAND.gold,
                      direction: "rtl",
                    }}
                  >
                    {f.ar}
                  </div>
                </div>
                <SerifHeading
                  level={3}
                  className="mb-3"
                  style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}
                >
                  {f.t}
                </SerifHeading>
                <p className="text-sm" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                  {f.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          ADMIN TOOLKIT
          ══════════════════════════════════ */}
      <section style={{ background: palette.ivory }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 max-w-3xl">
            <Eyebrow>Admin toolkit</Eyebrow>
            <SerifHeading
              className="mt-5 mb-5"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 4rem)",
                lineHeight: 1,
                letterSpacing: "-0.03em",
              }}
            >
              Quick actions, right where{" "}
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>you need them.</em>
            </SerifHeading>
            <p className="text-base sm:text-lg" style={{ color: palette.ink2, lineHeight: 1.55 }}>
              The same quick actions you'll see inside the Masjid Dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {TOOLKIT.map((tk) => (
              <EditorialCard
                key={tk.t}
                variant="paper"
                className="flex flex-col gap-4 p-7 sm:p-8"
                style={{ minHeight: 220 }}
              >
                <div className="flex items-start justify-between gap-3">
                  <SerifHeading
                    level={3}
                    style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.1 }}
                  >
                    {tk.t}
                  </SerifHeading>
                  {tk.badge && (
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase"
                      style={{
                        background: BRAND.gold + "22",
                        color: BRAND.goldDeep,
                        letterSpacing: "0.12em",
                      }}
                    >
                      {tk.badge}
                    </span>
                  )}
                </div>
                <p className="text-sm" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                  {tk.b}
                </p>
              </EditorialCard>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          PLANS
          ══════════════════════════════════ */}
      <section style={{ background: BRAND.ink, color: BRAND.ivory }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 text-center">
            <Eyebrow color={BRAND.gold}>Plans for your masjid</Eyebrow>
            <SerifHeading
              className="mt-5 mb-4"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 4rem)",
                lineHeight: 1.02,
              }}
            >
              Free forever.{" "}
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>Pro when you're ready.</em>
            </SerifHeading>
            <p
              className="mx-auto max-w-xl text-sm sm:text-base"
              style={{ color: "#c8bfa8", lineHeight: 1.55 }}
            >
              Based on the in-app plans outlined for masjid admins.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
            {PLANS.map((p) => (
              <div
                key={p.n}
                className="relative overflow-hidden rounded-3xl p-8 sm:p-10"
                style={{
                  background: p.dark ? BRAND.gold : "#1f1a15",
                  color: p.dark ? BRAND.ink : BRAND.ivory,
                  border: p.dark ? "none" : "1px solid #2a2520",
                }}
              >
                <div
                  className="mb-2 text-[11px] font-semibold uppercase"
                  style={{ letterSpacing: "0.16em", opacity: 0.75 }}
                >
                  {p.tag}
                </div>
                <div className="mb-7 flex items-baseline gap-3">
                  <div
                    style={{
                      fontFamily: EDITORIAL_FONT.serif,
                      fontSize: 52,
                      fontWeight: 500,
                      letterSpacing: "-0.03em",
                      lineHeight: 1,
                    }}
                  >
                    {p.n}
                  </div>
                  <div
                    style={{
                      fontFamily: EDITORIAL_FONT.serif,
                      fontStyle: "italic",
                      fontSize: 26,
                      fontWeight: 400,
                    }}
                  >
                    {p.price}
                  </div>
                </div>
                <div
                  className="mb-5"
                  style={{ height: 1, background: p.dark ? `${BRAND.ink}20` : "#2a2520" }}
                />
                {p.feats.map((f) => (
                  <div key={f} className="flex items-baseline gap-3 py-1.5 text-sm">
                    <span style={{ color: p.dark ? BRAND.ink : BRAND.gold, fontSize: 10 }}>◆</span>
                    <span>{f}</span>
                  </div>
                ))}
                <div className="mt-7">
                  <PillButton
                    variant="light"
                    onClick={handlePrimaryCta}
                    className="w-full justify-center"
                    style={
                      p.dark
                        ? { background: BRAND.ink, color: BRAND.ivory, border: "none" }
                        : { background: BRAND.ivory, color: BRAND.ink, border: "none" }
                    }
                  >
                    {p.cta}
                    <span aria-hidden>→</span>
                  </PillButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          WHAT YOU CAN DO DAY ONE
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 max-w-3xl">
            <Eyebrow>What you can do day one</Eyebrow>
            <SerifHeading
              className="mt-5 mb-5"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 4rem)",
                lineHeight: 1,
                letterSpacing: "-0.03em",
              }}
            >
              No long onboarding.{" "}
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>Start where you are.</em>
            </SerifHeading>
            <p className="text-base sm:text-lg" style={{ color: palette.ink2, lineHeight: 1.55 }}>
              Start with the same capabilities shown in the mobile Masjid screen and web dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {DAY_ONE.map((d, i) => (
              <div key={d.t} className="relative">
                <SerifHeading
                  level={3}
                  className="mb-3"
                  style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}
                >
                  {d.t}
                </SerifHeading>
                <p className="text-[15px]" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                  {d.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA — READY TO LAUNCH
          ══════════════════════════════════ */}
      <section style={{ background: palette.ivory, borderTop: `1px solid ${palette.hairline}` }}>
        <div
          className="mx-auto px-4 py-20 text-center sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <SerifHeading
            className="mb-6"
            style={{
              fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
              lineHeight: 0.98,
            }}
          >
            Ready to launch{" "}
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>your masjid?</em>
          </SerifHeading>
          <p className="mx-auto mb-8 max-w-xl text-[17px]" style={{ color: palette.ink2 }}>
            Create a free account, add your masjid, and start managing prayer times, events, and
            announcements today.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <PillButton variant="primary" onClick={handlePrimaryCta}>
              Get started
              <span aria-hidden>→</span>
            </PillButton>
            <PillButton variant="ghost" onClick={() => navigate("/login")}>
              Already an admin? Sign in
            </PillButton>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          REASSURANCE STRIP
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper, borderTop: `1px solid ${palette.hairline}` }}>
        <div
          className="mx-auto grid gap-10 px-4 py-16 sm:px-6 sm:py-20 md:grid-cols-3 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          {REASSURANCE.map((r) => (
            <div key={r.t}>
              <SerifHeading
                level={3}
                className="mb-3"
                style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15 }}
              >
                {r.t}
              </SerifHeading>
              <p className="text-[15px]" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                {r.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════
          FOOTER
          ══════════════════════════════════ */}
      <footer
        className="relative"
        style={{
          background: palette.paper,
          borderTop: `1px solid ${palette.hairline}`,
          color: palette.ink,
        }}
      >
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-10">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <span
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Muslim<span style={{ color: BRAND.gold }}>+</span>
                </span>
              </div>
              <p
                className="mb-6 max-w-md text-[15px] leading-relaxed"
                style={{ color: palette.muted }}
              >
                Connecting Muslims with their community through technology and faith. Find prayer
                times, discover nearby masajid, stay updated with events
                {halalEnabled ? ", and explore halal restaurants." : "."}
              </p>
              <div className="flex flex-wrap gap-2">
                {["Trusted Platform", "Verified Content", "Real-time Updates"].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium"
                    style={{
                      background: "transparent",
                      border: `1px solid ${palette.hairline}`,
                      color: palette.muted,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Quick links
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <button
                  onClick={() => navigate("/masjids")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Find Masajid
                </button>
                <button
                  onClick={() => navigate("/events-public")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Browse Events
                </button>
                {user && userProfile ? (
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="text-left transition-colors hover:text-[#bf9a2f]"
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    className="text-left transition-colors hover:text-[#bf9a2f]"
                  >
                    Sign In
                  </button>
                )}
                <button
                  onClick={() => navigate("/policy")}
                  className="text-left transition-colors hover:text-[#bf9a2f]"
                >
                  Privacy Policy
                </button>
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Resources
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download iOS App
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Download Android App
                </a>
                <a
                  href="https://www.instagram.com/muslimplus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Follow us on Instagram
                </a>
              </div>
            </div>
          </div>

          <div
            className="mt-12 flex flex-col items-start justify-between gap-3 border-t pt-8 text-xs md:flex-row md:items-center"
            style={{ borderColor: palette.hairline, color: palette.muted }}
          >
            <span>© {new Date().getFullYear()} Muslim+. All rights reserved.</span>
            <span>Made with ❤️ for the Muslim community</span>
          </div>
        </div>
      </footer>
    </EditorialShell>
  );
}
